// i18nSetup.ts
/* eslint-disable @typescript-eslint/no-var-requires, global-require, import/no-named-as-default-member */

import i18n, { Resource, ResourceLanguage } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import PubSub from 'pubsub-js';
import { initReactI18next } from 'react-i18next';

import { EnvironmentService } from '../../services/environment/environment.service';
import { SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { languageDropdownListItems, SupportedLanguage } from '../text/translation';
import GoogleTranslateLanguageDetector from './google-translate-language-detector';

type ResourceLoader = () => Promise<Resource> | Resource;
type LanguagePreChangeHook = (lng: SupportedLanguage) => Promise<void>;

let customResourceLoader: ResourceLoader | undefined;
let languagePreChangeHook: LanguagePreChangeHook | undefined;

export const setI18nResourceLoader = (loader: ResourceLoader) => {
  customResourceLoader = loader;
};

export const setLanguagePreChangeHook = (hook: LanguagePreChangeHook) => {
  languagePreChangeHook = hook;
};

const loadResourcesFromFilesystem = (): Resource => {
  const resources: Resource = {};

  for (const { value: lng } of languageDropdownListItems) {
    const languageResources: ResourceLanguage = {
      apiErrorCodes: require(
        `../../../locales/${lng}/apiErrorCodes.yaml`,
      ) as ResourceLanguage[string],
      cloudProcessing: require(
        `../../../locales/${lng}/cloudProcessing.yaml`,
      ) as ResourceLanguage[string],
      navigation: require(`../../../locales/${lng}/navigation.yaml`) as ResourceLanguage[string],
      components: require(`../../../locales/${lng}/components.yaml`) as ResourceLanguage[string],
      common: require(`../../../locales/${lng}/common.yaml`) as ResourceLanguage[string],
      notifications: require(
        `../../../locales/${lng}/notifications.yaml`,
      ) as ResourceLanguage[string],
      skyviewAsbuilt: require(
        `../../../locales/${lng}/skyviewAsbuilt.yaml`,
      ) as ResourceLanguage[string],
      skyviewAssetMenu: require(
        `../../../locales/${lng}/skyviewAssetMenu.yaml`,
      ) as ResourceLanguage[string],
      skyviewDesign: require(
        `../../../locales/${lng}/skyviewDesign.yaml`,
      ) as ResourceLanguage[string],
      companies: require(`../../../locales/${lng}/companies.yaml`) as ResourceLanguage[string],
      skyviewScale: require(
        `../../../locales/${lng}/skyviewScale.yaml`,
      ) as ResourceLanguage[string],
      skyviewToolbar: require(
        `../../../locales/${lng}/skyviewToolbar.yaml`,
      ) as ResourceLanguage[string],
      skyviewVolumeCalculation: require(
        `../../../locales/${lng}/skyviewVolumeCalculation.yaml`,
      ) as ResourceLanguage[string],
      skyviewReport: require(
        `../../../locales/${lng}/skyviewReport.yaml`,
      ) as ResourceLanguage[string],
      skyview: require(`../../../locales/${lng}/skyview.yaml`) as ResourceLanguage[string],
      integrations: require(
        `../../../locales/${lng}/integrations.yaml`,
      ) as ResourceLanguage[string],
      profile: require(`../../../locales/${lng}/profile.yaml`) as ResourceLanguage[string],
    };

    resources[lng] = languageResources;
  }

  return resources;
};

/**
 *  Initializes i18next and loads the language files.
 */
export const initializeI18n = async (): Promise<void> => {
  if (i18n.isInitialized) {
    return;
  }

  const resources: Resource = customResourceLoader
    ? await customResourceLoader()
    : loadResourcesFromFilesystem();

  const languageDetector = new LanguageDetector();
  languageDetector.addDetector(GoogleTranslateLanguageDetector);

  i18n.on('languageChanged', () => {
    PubSub.publish(SubscriptionTopic.I18NextLanguageChanged, {});
  });

  await i18n
    .use(ICU)
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      // Enable debugging only for development and developer stacks. Not for prod environment or
      // during Jest tests.
      debug: EnvironmentService.instance.getEnvironment() === 'development',

      resources,

      // Fallback to English translation for keys not found in another language.
      fallbackLng: 'en',

      detection: {
        order: ['googleTranslateCookie'],
        caches: ['googleTranslateCookie'],
      },

      // We don't currently use https://react.dev/reference/react/Suspense.
      react: {
        useSuspense: false,
      },
    });
};

/**
 *  Sets the current language that should be used by i18next.
 *
 *  If i18next is not initialized, the language is not set.
 */
export const setLanguage = async (lng: SupportedLanguage) => {
  if (!i18n.isInitialized) {
    return;
  }

  if (languagePreChangeHook) {
    await languagePreChangeHook(lng);
  }

  await i18n.changeLanguage(lng);
};

/**
 *  Exports the 't' function from the initialized i18next instance for consistent localization.
 *  This enables sharing the same instance across various modules, such as JavaScript files.
 */
export const t = i18n.t;
